import { useEffect, useState } from "react";
import { BsXLg } from "react-icons/bs";
import { useSelector } from "react-redux";
import { iActionProps } from "../../interfaces/iEnquiry";
import { GetMailboxPermissions } from "../../interfaces/Permissions";
import Statuses from "../../interfaces/Statuses";
import { ConvertSecondsToHHMMSS, FormatDateTimeDDMMYYHHMMSS } from "../../Logic/DateTimeFormatters";
import {
    GetEnquiryProperties,
    iEnquiryDetails,
    iEnquiryProperties,
    UpdateEnquiryDetails,
} from "../../Logic/EmsAPI/Enquiries";
import { RootState } from "../../state/store";

import { default as Confirm, default as ConfirmDialog } from "../Generic/ConfirmDialog";
import { ActiveTabName } from "./ActiveTabName";
import DatesAndTimes from "./DatesAndTimes";
import General from "./General";
import "./index.scss";
import MIS from "./MIS";
import PropertiesTab from "./PropertiesTab";

interface Props extends iActionProps {
    enquiryId: number;
}

export interface iEnquiryPropertiesProps {
    enquiryDetails?: iEnquiryProperties;
    handleChange: <K extends keyof iEnquiryDetails>(newValue: iEnquiryDetails[K], key: K) => void;
    updateRestricted: boolean;
}

export function EnquiryPropertiesModal(props: Props) {
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [activeTab, setActiveTab] = useState<ActiveTabName>(ActiveTabName.general);
    const [enquiryDetails, setGeneral] = useState<iEnquiryProperties | undefined>(undefined);
    const permissions = useSelector((state: RootState) => state.permissions);
    const updateRestricted =
        !enquiryDetails ||
        !GetMailboxPermissions(permissions, enquiryDetails!.mailbox).ModifyEnquiryProperties ||
        enquiryIsProcessing();
    const [isUserProcessing, setIsUserProcessing] = useState(false);
    const [Dirty, setDirty] = useState(false);

    function enquiryIsProcessing(): boolean {
        return enquiryDetails?.status === Statuses.Processing.ID;
    }
    useEffect(() => {
        (async () => {
            await getEnquiryDetail(props.enquiryId);
        })();
    }, [props.enquiryId]);

    const handleChange = <K extends keyof iEnquiryDetails>(newValue: iEnquiryDetails[K], key: K) => {
        setGeneral(g => ({ ...g!, [key]: newValue }));
        setDirty(true);
    };

    async function UpdateFields() {
        if (!Dirty) props.onCancel();
        else if (enquiryDetails) {
            const response = await UpdateEnquiryDetails(enquiryDetails!);

            if (!response.isOk) {
                if (
                    response.status == 400 &&
                    response.validationErrors?.errors?.EnquiryID?.find(
                        errorMessage => errorMessage == "Enquiry is in processing.",
                    )
                ) {
                    setIsUserProcessing(true);
                } else props.onError(response.statusText, response.errorLogID);
            } else {
                props.onCreated();
            }
        }
    }

    async function getEnquiryDetail(enquiryId: number) {
        const response = await GetEnquiryProperties(enquiryId);
        if (!response.isOk) {
            throw new Error(`${response.status} returned from server.`);
        }
        if (response.data) {
            setGeneral(response.data);
        }
    }

    return (
        <>
            <div className="enquiry-properties">
                <div className="enquiry-properties-content">
                    <div className="enquiry-properties-tabs-content">
                        <div className="tabs-header">Enquiry Properties</div>
                        <PropertiesTab
                            title={ActiveTabName.general}
                            onClick={() => setActiveTab(ActiveTabName.general)}
                            active={activeTab === ActiveTabName.general}
                        />
                        <PropertiesTab
                            title={ActiveTabName.datesTimes}
                            onClick={() => setActiveTab(ActiveTabName.datesTimes)}
                            active={activeTab === ActiveTabName.datesTimes}
                        />
                        <PropertiesTab
                            title={ActiveTabName.owners}
                            onClick={() => setActiveTab(ActiveTabName.owners)}
                            active={activeTab === ActiveTabName.owners}
                        />
                        <PropertiesTab
                            title={ActiveTabName.alarms}
                            onClick={() => setActiveTab(ActiveTabName.alarms)}
                            active={activeTab === ActiveTabName.alarms}
                        />
                        <PropertiesTab
                            title={ActiveTabName.history}
                            onClick={() => setActiveTab(ActiveTabName.history)}
                            active={activeTab === ActiveTabName.history}
                        />
                        <PropertiesTab
                            title={ActiveTabName.production}
                            onClick={() => setActiveTab(ActiveTabName.production)}
                            active={activeTab === ActiveTabName.production}
                        />
                        <PropertiesTab
                            title={ActiveTabName.userCycle}
                            onClick={() => setActiveTab(ActiveTabName.userCycle)}
                            active={activeTab === ActiveTabName.userCycle}
                        />
                        <PropertiesTab
                            title={ActiveTabName.response}
                            onClick={() => setActiveTab(ActiveTabName.response)}
                            active={activeTab === ActiveTabName.response}
                        />
                        <PropertiesTab
                            title={ActiveTabName.mis}
                            onClick={() => setActiveTab(ActiveTabName.mis)}
                            active={activeTab === ActiveTabName.mis}
                        />
                    </div>
                    <div className="enquiry-properties-main-content">
                        <section className="header-section">
                            <div className="header">{activeTab}</div>
                            <i
                                className="x"
                                onClick={() => setConfirmCancel(true)}
                            >
                                <BsXLg className="close-bttn" />
                            </i>
                        </section>
                        <section className="body">
                            <div className="confirm-message-body">
                                <div style={{ width: "100%" }}>
                                    {activeTab === ActiveTabName.general && enquiryDetails && (
                                        <General
                                            enquiryDetails={enquiryDetails}
                                            handleChange={handleChange}
                                            updateRestricted={updateRestricted}
                                        />
                                    )}
                                    {activeTab === ActiveTabName.datesTimes && enquiryDetails && (
                                        <DatesAndTimes
                                            enquiryDetails={enquiryDetails}
                                            handleChange={handleChange}
                                            updateRestricted={updateRestricted}
                                        />
                                    )}
                                    {activeTab === ActiveTabName.owners && enquiryDetails && (
                                        <div>
                                            {enquiryDetails.owners.map(owner => {
                                                return (
                                                    <div key={"Main" + owner.user.id}>
                                                        <div className="row rowMarginControl">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Name</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {owner.user.lastname}, {owner.user.firstname}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row rowMarginControl">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Assigned By</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {owner.assignedBy
                                                                        ? `${owner.assignedBy.lastname}, ${owner.assignedBy.firstname}`
                                                                        : ""}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row rowMarginControl">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Assigned Date</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {owner.assignedAt
                                                                        ? FormatDateTimeDDMMYYHHMMSS(owner.assignedAt)
                                                                        : ""}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <hr />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {activeTab === ActiveTabName.alarms && enquiryDetails && (
                                        <div>
                                            <table
                                                cellPadding={0}
                                                cellSpacing={0}
                                            >
                                                <tr>
                                                    <td style={{ width: "50%" }}>
                                                        <b>User</b>
                                                    </td>
                                                    <td style={{ width: "50%" }}>
                                                        <b>Snooze Until</b>
                                                    </td>
                                                </tr>
                                                {enquiryDetails.alarms.map(x => {
                                                    return (
                                                        <tr key={"Main" + x.id}>
                                                            <td>
                                                                <label className="g-label">
                                                                    {x.user.firstname} {x.user.lastname}
                                                                </label>
                                                            </td>
                                                            <td>
                                                                <label className="g-label">
                                                                    {x.alarmAt
                                                                        ? FormatDateTimeDDMMYYHHMMSS(x.alarmAt)
                                                                        : ""}
                                                                </label>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}{" "}
                                            </table>
                                        </div>
                                    )}
                                    {activeTab === ActiveTabName.history && enquiryDetails && (
                                        <div
                                            style={{
                                                height: "420px",
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                            }}
                                        >
                                            {enquiryDetails.logEntries.map(x => {
                                                return (
                                                    <>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Date</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {FormatDateTimeDDMMYYHHMMSS(x.dateTime)}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Status</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.statusName ?? "No Status"}
                                                                </label>
                                                            </div>
                                                        </div>

                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">User</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.updatedBy &&
                                                                    x.updatedBy.firstname + " " + x.updatedBy.lastname
                                                                        ? x.updatedBy.firstname +
                                                                          " " +
                                                                          (x.updatedBy.lastname
                                                                              ? x.updatedBy.lastname
                                                                              : "")
                                                                        : ""}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <hr></hr>
                                                        </div>
                                                    </>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {activeTab === ActiveTabName.production && enquiryDetails && (
                                        <div
                                            style={{
                                                height: "420px",
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                            }}
                                        >
                                            {enquiryDetails.slaProductivity.map(x => {
                                                return (
                                                    <>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">User</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.user && x.user.firstname + " " + x.user.lastname
                                                                        ? x.user.firstname +
                                                                          " " +
                                                                          (x.user.lastname ? x.user.lastname : "")
                                                                        : ""}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Wizard Entry</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.wizardEntry
                                                                        ? FormatDateTimeDDMMYYHHMMSS(x.wizardEntry)
                                                                        : ""}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Status Entry</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.entryStatusName ? x.entryStatusName : ""}
                                                                </label>
                                                            </div>
                                                        </div>

                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Wizard Exit</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.wizardExit
                                                                        ? FormatDateTimeDDMMYYHHMMSS(x.wizardExit)
                                                                        : ""}
                                                                </label>
                                                            </div>
                                                        </div>

                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Status Exit</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.exitStatusName ? x.exitStatusName : ""}
                                                                </label>
                                                            </div>
                                                        </div>

                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Achieved Time</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.timeAchieved !== undefined ? x.timeAchieved : ""}
                                                                </label>
                                                            </div>
                                                        </div>

                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Work Items</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.workItems !== undefined ? x.workItems : ""}
                                                                </label>
                                                            </div>
                                                        </div>

                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Has Outbound</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.hasOutbound !== undefined
                                                                        ? x.hasOutbound
                                                                            ? "Yes"
                                                                            : "No"
                                                                        : ""}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <hr></hr>
                                                    </>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {activeTab === ActiveTabName.userCycle && enquiryDetails && (
                                        <div
                                            style={{
                                                height: "420px",
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                            }}
                                        >
                                            {enquiryDetails.slaUsers.map(x => {
                                                return (
                                                    <>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">User</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.user && x.user.firstname + " " + x.user.lastname
                                                                        ? x.user.firstname +
                                                                          " " +
                                                                          (x.user.lastname ? x.user.lastname : "")
                                                                        : "No User"}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Started</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.dateStarted
                                                                        ? FormatDateTimeDDMMYYHHMMSS(x.dateStarted)
                                                                        : " "}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Completed</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.dateCompleted
                                                                        ? FormatDateTimeDDMMYYHHMMSS(x.dateCompleted)
                                                                        : " "}
                                                                </label>
                                                            </div>
                                                        </div>{" "}
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Time Achieved</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.timeAchieved !== undefined
                                                                        ? x.timeAchieved
                                                                        : " "}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Timeout</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.slaTimeout
                                                                        ? FormatDateTimeDDMMYYHHMMSS(x.slaTimeout)
                                                                        : " "}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Status</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.statusName ? x.statusName : ""}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <hr></hr>
                                                    </>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {activeTab === ActiveTabName.response && enquiryDetails && (
                                        <div
                                            style={{
                                                height: "420px",
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                            }}
                                        >
                                            {enquiryDetails.slaResponses.map(x => {
                                                return (
                                                    <>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">User</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.user && x.user.firstname + " " + x.user.lastname
                                                                        ? x.user.firstname +
                                                                          " " +
                                                                          (x.user.lastname ? x.user.lastname : "")
                                                                        : "No User"}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Started</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.responseStart
                                                                        ? FormatDateTimeDDMMYYHHMMSS(x.responseStart)
                                                                        : " "}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Completed</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.responseStop
                                                                        ? FormatDateTimeDDMMYYHHMMSS(x.responseStop)
                                                                        : " "}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="row  ">
                                                            <div className={"col-sm-4"}>
                                                                <label className="g-label">Time Achieved</label>
                                                            </div>
                                                            <div className={"col-sm-8"}>
                                                                <label className="g-label">
                                                                    {x.responseAchieved !== undefined
                                                                        ? ConvertSecondsToHHMMSS(x.responseAchieved)
                                                                        : " "}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <hr></hr>
                                                    </>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {activeTab === ActiveTabName.mis && enquiryDetails && (
                                        <div
                                            style={{
                                                height: "420px",
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                            }}
                                        >
                                            <MIS
                                                enquiryDetails={enquiryDetails}
                                                handleChange={handleChange}
                                                updateRestricted={updateRestricted}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>
                        <section className="buttons-section">
                            <div className="buttons-wrapper">
                                <button
                                    key="dialog-confirm"
                                    className={"g-btn g-btn-action"}
                                    onClick={UpdateFields}
                                >
                                    Ok
                                </button>
                                <button
                                    key="dialog-cancel"
                                    className={"g-btn g-btn-alternate"}
                                    onClick={() => {
                                        if (!Dirty) props.onCancel();
                                        else setConfirmCancel(true);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
            {confirmCancel && (
                <Confirm
                    onCancel={() => setConfirmCancel(false)}
                    title="Warning"
                    onConfirm={props.onCancel}
                >
                    <>Unsaved changes will be lost. Are you sure that you wish to close?</>
                </Confirm>
            )}
            {isUserProcessing && Dirty && (
                <ConfirmDialog
                    title="Warning"
                    onConfirm={() => {
                        setIsUserProcessing(false);
                    }}
                >
                    <>
                        This enquiry is currently being worked by another user. <br />
                        Your changes could not be saved.
                    </>
                </ConfirmDialog>
            )}
        </>
    );
}
